import { Injectable } from '@angular/core';
import { ApiClient, BenutzerProfil, BenutzerProfilFavorit, Lieferant, ObjektTyp } from 'app/core/services/api-client';
import { ApiErrorHandlerService } from 'app/core/services/api-default-error-handler/api-error-handler.service';
import { Observable, of as ObservableOf } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ClientInfo } from './models/clientInfo';
import { FavoriteMerkmal } from '@models/dossier/dossier-models';
import * as _ from 'lodash';

@Injectable()
export class BenutzerprofilService {
  private MAX_FAVOURITE_MERKMALE = 15;

  public constructor(
    private apiClient: ApiClient,
    private errorHandler: ApiErrorHandlerService,
  ) {}

  public loadUserProfile(): Observable<BenutzerProfil> {
    return this.apiClient.apiBenutzerProfilGet().pipe(
      catchError(
        this.apiClient.apiBenutzerProfilGetErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }

  public loadMeinLieferant(): Observable<Lieferant> {
    return this.apiClient.apiMeinLieferantGet().pipe(
      catchError(
        this.apiClient.apiMeinLieferantGetErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
          error404Handler: error404 =>
            this.errorHandler.handle404Error(error404, {
              messageKey: 'errors.404.apiMeinLieferantGet', // Lieferant existiert nicht
            }),
        }),
      ),
    );
  }

  public checkFavorit(objektTyp: ObjektTyp, objektId: number): Observable<BenutzerProfilFavorit[]> {
    return this.apiClient.apiBenutzerProfilFavoritenGet(objektTyp, objektId).pipe(
      catchError(
        this.apiClient.apiBenutzerProfilFavoritenGetErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }

  public loadFavoriten(): Observable<BenutzerProfilFavorit[]> {
    return this.apiClient.apiBenutzerProfilFavoritenGet().pipe(
      catchError(
        this.apiClient.apiBenutzerProfilFavoritenGetErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }

  public addFavorit(objektTyp: ObjektTyp, objektId: number): Observable<BenutzerProfilFavorit> {
    return this.apiClient.apiBenutzerProfilFavoritenPost(new BenutzerProfilFavorit({ objektId: objektId, objektTyp: objektTyp })).pipe(
      catchError(
        this.apiClient.apiBenutzerProfilFavoritenPostErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }

  public removeFavorit(objektTyp: ObjektTyp, objektId: number): Observable<void> {
    return this.apiClient.apiBenutzerProfilFavoritenByTypByIdPost(objektTyp, objektId).pipe(
      catchError(
        this.apiClient.apiBenutzerProfilFavoritenByTypByIdPostErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }

  public addFavouriteMerkmal(merkmal: FavoriteMerkmal): Observable<FavoriteMerkmal[]> {
    return this.saveFavouriteMerkmal(merkmal);
  }
  private saveFavouriteMerkmal(merkmal: FavoriteMerkmal): Observable<FavoriteMerkmal[]> {
    let localFavouriteMerkmale = (JSON.parse(localStorage.getItem('favouriteMerkmale')) as FavoriteMerkmal[]) || [];
    localFavouriteMerkmale = localFavouriteMerkmale.filter(m => m.key !== merkmal.key);
    localFavouriteMerkmale = _.uniqBy(localFavouriteMerkmale, 'key');
    merkmal.timestamp = new Date();
    localFavouriteMerkmale.push(merkmal);
    localFavouriteMerkmale = _.orderBy(localFavouriteMerkmale, function (m) {
      return -new Date(m.timestamp).getTime();
    });
    localFavouriteMerkmale = localFavouriteMerkmale.slice(0, this.MAX_FAVOURITE_MERKMALE);
    const newFavouriteMerkmale = [...new Set(localFavouriteMerkmale)];
    localStorage.setItem('favouriteMerkmale', JSON.stringify(newFavouriteMerkmale));
    return ObservableOf(newFavouriteMerkmale);
  }

  public saveClientInfo(clientInfo: ClientInfo): Observable<void> {
    return this.apiClient.apiBenutzerProfilClientStatePut(clientInfo).pipe(
      catchError(
        this.apiClient.apiBenutzerProfilClientStatePutErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }
}
